import { createFileRoute, retainSearchParams, useParams, Link, useMatch } from '@tanstack/react-router'
import { SectionAssistant } from './-components/assistant/assistant-frame'
import { CRUMB_LINK_CLASS } from './-components/title-section'
import type { RootSearch } from '@/routes/__root'
import { Suspense, useState, useDeferredValue } from 'react'
import { useSuspenseQuery, useSuspenseInfiniteQuery } from '@tanstack/react-query'
import { usePermissions } from '@/hooks/use-permissions'
import { useLingui } from '@lingui/react/macro'
import { BriefcaseIcon, SearchIcon, PlusIcon, FileTextIcon } from 'lucide-react'
import { useModalLink, useModalMatch } from '@/components/custom/modal-slot'
import { clearProfileSelection, useRootSearch } from '@/hooks/use-root-search'
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll'
import { useHydrationSafePages } from '@/hooks/use-hydrating-render'
import { useDebouncedSearch } from '@/hooks/use-debounced-search'
import { ButtonLink } from '@/components/ui/button'
import { SearchInput } from '@/components/ui/search-input'
import { SectionLabel } from '@/components/custom/section-label'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { NAVIGATION_ICONS } from './-components/shared/navigation-config'
import { SectionIconTile } from './-components/shared/section-icon'
import { AnimatedOutlet } from './-components/animated-outlet'
import {
  usePreviewMode,
  useIsLivePane,
  NavStackStage,
  NavStackScreen,
  useStackedLayout,
  NS_PASSTHROUGH_CLASS,
} from '@/components/custom/route-transition'
import { SectionStack, useSectionListHidden } from './-components/shared/section-stack'
import { ReportScreenTitle } from './-components/report/report-screen-title'
import { MembersPanelSkeleton, ReportPanelSkeleton } from './-components/app-layout-skeleton'
import { ListBodyPending } from './-components/shared/list-body-pending'
import { ListPanePending } from './-components/shared/list-pane-pending'
import {
  PageLayout,
  StickyPanel,
  ScrollableContent,
  ScrollableArea,
  SlideInPanel,
  CollapsibleListPanel,
} from './-components/shared/layout'
import {
  CollapsiblePanelProvider,
  CollapsiblePanel,
  CollapsiblePanelContent,
} from './-components/shared/collapsible-panel'
import { ScrollableList } from './-components/shared/scrollable-list'
import { SelectionHint, CollapsibleSectionLabel, CollapsibleToolbar } from './-components/shared/collapsible'
import { PanelEmptyState, PanelSelectionPrompt } from './-components/shared/panel-empty-state'
import { OfferTreeItem } from './-components/offers/offer-tree-item'
import { OfferActionMenu } from './-components/offers/offer-action-menu'
import { offerDetail } from './-components/offers/offer-detail'
import {
  ApplicantOffersPanelContent,
  useApplicantOffersInfinite,
} from './-components/applicants/applicant-offers-panel-content'
import { IncompleteOfferAutoOpen } from './-components/offers/incomplete-offer-auto-open'

const OffersApplicantsPanel = offerDetail.pane((panes) => panes.OffersApplicantsPanel)
const OfferReportContent = offerDetail.pane((panes) => panes.OfferReportContent)
const ReportPager = offerDetail.pane((panes) => panes.ReportPager)

export const Route = createFileRoute('/_app/offers')({
  search: {
    middlewares: [retainSearchParams<RootSearch>(true)],
  },
  component: RouteComponent,
  pendingComponent: ListPanePending,
  // Painted on the click frame; safe only because this match never re-keys — the search-keyed list lives on the
  // pathless `_list` layout below (see offers._list.tsx), so a keystroke cannot swap this rail for the skeleton.
})

function RouteComponent() {
  // Company users — and a promoted admin/authorized employee in company view (Block C) — get the
  // three-panel company layout; everyone else (applicants) gets the two-panel offers list + report.
  const { isCompanyView } = usePermissions()
  if (isCompanyView) {
    return <CompanyOffersView />
  }
  return <ApplicantOffersView />
}

function CompanyOffersView() {
  const isLivePane = useIsLivePane()
  const { t } = useLingui()

  // Inside a report-tab live preview (/offers/<id>/board …), strip to just the pager pane.
  if (usePreviewMode('/offers') === 'passthrough') {
    return (
      <Suspense fallback={null}>
        <ReportPager className="h-full" />
      </Suspense>
    )
  }

  return (
    <div id={isLivePane ? 'report' : undefined} className="@container flex h-full flex-col">
      {/* On a phone the three panes below are STACKED SCREENS, not a squashed row: the offers list, the offer's
          candidates, and the report, each pushed in from the right over the last (see nav-stack.tsx).
          The provider sits above PageLayout so the title bar can carry the stack's back chevron. */}
      <OffersStack>
        <PageLayout
          icon={NAVIGATION_ICONS.offers}
          title={t`Offers`}
          breadcrumbsSlot={<OffersBreadcrumbs />}
          titleActions={<OffersTitleActions />}
        >
          <OffersLayout />
        </PageLayout>
      </OffersStack>
    </div>
  )
}

/**
 * Offers' mobile hierarchy — list → the offer's candidates → its report. The standard section shape, so the
 * whole declaration is the three routes involved; `SectionStack` owns the depth derivation and the pop policy.
 */
function OffersStack(props: { children: React.ReactNode }) {
  const { offerId } = useParams({ strict: false })
  return (
    <SectionStack
      scopedEmbed
      list={{ to: '/offers' }}
      detail={offerId ? { to: '/offers/$offerId', params: { offerId } } : null}
    >
      {props.children}
    </SectionStack>
  )
}

function ApplicantOffersView() {
  const isLivePane = useIsLivePane()
  const { t } = useLingui()
  return (
    <div id={isLivePane ? 'report' : undefined} className="@container flex h-full flex-col">
      <PageLayout icon={NAVIGATION_ICONS.offers} title={t`Offers`} breadcrumbsSlot={<OffersBreadcrumbs />}>
        <ApplicantOffersLayout />
      </PageLayout>
    </div>
  )
}

function OffersLayout() {
  const { t } = useLingui()
  const { offerId } = useParams({ strict: false })
  const hasOfferSelected = !!offerId
  // A token link / SDK embed opened on one offer is scoped to it: no rail, and the offer is the stack's root.
  const { listHidden, level } = useSectionListHidden()
  const describeRequested = useModalMatch('/@modal/jobs/$jobId/describe/{-$indicator}') !== undefined

  // No viewport-driven auto-collapse any more: the nav stack gives each pane the WHOLE screen and decides
  // which one is on it, so both providers stay expanded and the collapse is desktop's business only
  // (where `isMobile` was false regardless — this is a no-op there).
  return (
    <CollapsiblePanelProvider defaultCollapsed={false}>
      {/* Mirrors jobs.tsx: a describe link asked for the wizard, so the completion nag stays out of its way. */}
      {offerId && !describeRequested && (
        <Suspense fallback={null}>
          <IncompleteOfferAutoOpen />
        </Suspense>
      )}

      {/* The panel row on desktop, the stack's stage on mobile — same div, same classes, see
          NavStackStage. Each pane is wrapped in the level it occupies in the mobile hierarchy; on
          desktop those wrappers are `display: contents` and this lays out exactly as it always did. */}
      <NavStackStage className="flex h-full min-h-0 w-full md:relative" style={{ containerType: 'inline-size' }}>
        {/* LEVEL 0 — Offers list (always visible on desktop, squashes to a 52px icon column when an offer
            is selected; content keeps its full width inside and is clipped so only the avatars show) */}
        {!listHidden && (
          <NavStackScreen index={0} title={t`Offers`} leading={<SectionIconTile icon={NAVIGATION_ICONS.offers} />}>
            <CollapsibleListPanel isOpen={hasOfferSelected} className="print:hidden">
              <CollapsiblePanel expandedWidth="100%" disableTransition>
                {/* Single-list panel: the list view inside `CollapsiblePanelContent` is a `@container`
                    child of `SquashPanel`, so the same rendered list responsively collapses into a
                    centered avatar column (icon rail) at narrow widths via container queries. */}
                <CollapsiblePanelContent>
                  <Suspense fallback={<ListBodyPending />}>
                    <OffersPanelContent variant="sidebar" />
                  </Suspense>
                </CollapsiblePanelContent>
              </CollapsiblePanel>
            </CollapsibleListPanel>
          </NavStackScreen>
        )}

        {/* Right Panel Group (Middle + Detail) - width animates from 0 to target. `NS_PASSTHROUGH_CLASS`: on
            mobile it stops being a box so the two levels inside position against the stage, not against
            this group's (0px / clipped) width. */}
        <SlideInPanel isOpen={hasOfferSelected} innerClassName="flex" className={NS_PASSTHROUGH_CLASS}>
          {/* LEVEL 1 — the offer's candidates. The first screen an offer opens onto on mobile. */}
          <NavStackScreen
            index={level(1)}
            title={<OfferScreenTitle />}
            actions={offerId ? <OfferScreenActions offerId={offerId} /> : null}
          >
            <CollapsiblePanelProvider cookieName="panel_state_middle" keyboardShortcut="]" defaultCollapsed={false}>
              <StickyPanel className="print:hidden">
                <CollapsiblePanel>
                  <Suspense fallback={<MembersPanelSkeleton />}>
                    {offerId ? (
                      <OffersApplicantsPanel offerId={offerId} />
                    ) : (
                      <PanelSelectionPrompt text={t`Select an offer to view applicants`} />
                    )}
                  </Suspense>
                </CollapsiblePanel>
              </StickyPanel>
            </CollapsiblePanelProvider>
          </NavStackScreen>

          {/* LEVEL 2 — the report. The `offers.$offerId` layout route renders here via the report pager.
              It lives HERE (not in the `$offerId` layout) so the enter animation plays once when the report
              panel first appears and never re-animates on tab/offer switches. */}
          <NavStackScreen index={level(2)} title={<ReportScreenTitle />}>
            <ScrollableContent>
              {hasOfferSelected ? (
                <Suspense fallback={<ReportPanelSkeleton />}>
                  <OfferReportContent />
                </Suspense>
              ) : null}
            </ScrollableContent>
          </NavStackScreen>
        </SlideInPanel>
        {/* Nothing selected: the empty detail side holds the assistant's conversation. */}
        <SectionAssistant shown={!hasOfferSelected && !listHidden} listed={!listHidden} />
      </NavStackStage>
    </CollapsiblePanelProvider>
  )
}

/**
 * The title of the OFFER screen (nav stack level 1) — the offer's own name, the same thing the desktop
 * breadcrumb's second crumb shows. Suspends on `getJob`, which the `$offerId` layout has already prefetched,
 * so it fills in with the rest of the screen; the fallback keeps the bar's height either way.
 */
function OfferScreenTitle() {
  const { offerId } = useParams({ strict: false })
  if (!offerId) return null
  return (
    <Suspense fallback={null}>
      <OfferName />
    </Suspense>
  )
}

function OfferName() {
  // This renders in the LIST layout, above the `$offerId` match, so it has no context of its own for
  // this entity — it reads the OWNING layout's, matched exactly when the id is present, which is the
  // guard every call site already applies.
  const { context } = useMatch({ from: '/_app/offers/_list/$offerId' })
  const { data: offer } = useSuspenseQuery(context.jobQueryOptions)
  return <>{offer?.name ?? ''}</>
}

/**
 * The OFFER screen's header actions: the offer's own report (one push deeper, the same level a tapped
 * candidate lands on but with the offer selected instead of a person — on desktop that pane is always beside
 * the list, so there is nothing to reach for there) and the owner's action menu.
 */
function OfferScreenActions(props: { offerId: string }) {
  const { t } = useLingui()
  return (
    <>
      <ButtonLink
        variant="ghost"
        size="icon"
        aria-label={t`Offer report`}
        className="size-8"
        to="/offers/$offerId/report"
        params={{ offerId: props.offerId }}
        search={(prev) => ({ ...prev, second: undefined, selected: 'first' })}
      >
        <FileTextIcon className="size-4" />
      </ButtonLink>
      <Suspense fallback={null}>
        <OffersTitleActionsLoaded />
      </Suspense>
    </>
  )
}

function OffersBreadcrumbs() {
  const { t } = useLingui()
  const { offerId } = useParams({ strict: false })
  const { listHidden } = useSectionListHidden()
  // Scoped to one offer, there is no list to go back to: the offer's own crumb is the whole trail.
  if (listHidden && offerId) {
    return (
      <Breadcrumb>
        <BreadcrumbList>
          <Suspense fallback={null}>
            <OfferDetailCrumb offerId={offerId} leading />
          </Suspense>
        </BreadcrumbList>
      </Breadcrumb>
    )
  }
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink
            className={CRUMB_LINK_CLASS}
            render={
              <Link
                to="/offers"
                search={(prev) => ({
                  ...clearProfileSelection(prev),
                })}
              >
                {t`Offers`}
              </Link>
            }
          />
        </BreadcrumbItem>
        {offerId && (
          <Suspense fallback={null}>
            <OfferDetailCrumb offerId={offerId} />
          </Suspense>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  )
}

function OfferDetailCrumb(props: { offerId: string; leading?: boolean }) {
  // This renders in the LIST layout, above the `$offerId` match, so it has no context of its own for
  // this entity — it reads the OWNING layout's, matched exactly when the id is present, which is the
  // guard every call site already applies.
  const { context } = useMatch({ from: '/_app/offers/_list/$offerId' })
  const { data: offer } = useSuspenseQuery(context.jobQueryOptions)
  if (!offer?.name) return null
  return (
    <>
      {!props.leading && <BreadcrumbSeparator />}
      <BreadcrumbItem>
        <BreadcrumbLink
          className={CRUMB_LINK_CLASS}
          render={
            <Link
              to="/offers/$offerId/report"
              params={{ offerId: props.offerId }}
              search={(prev) => ({
                ...clearProfileSelection(prev),
              })}
            >
              {offer.name}
            </Link>
          }
        />
      </BreadcrumbItem>
    </>
  )
}

function OffersTitleActions() {
  const { offerId } = useParams({ strict: false })
  if (!offerId) return null
  return (
    <Suspense fallback={null}>
      <OffersTitleActionsLoaded />
    </Suspense>
  )
}

function OffersTitleActionsLoaded() {
  // This renders in the LIST layout, above the `$offerId` match, so it has no context of its own for
  // this entity — it reads the OWNING layout's, matched exactly when the id is present, which is the
  // guard every call site already applies.
  const { context } = useMatch({ from: '/_app/offers/_list/$offerId' })
  const { data: offer } = useSuspenseQuery(context.jobQueryOptions)
  return offer.viewerRole === 'owner' ? <OfferActionMenu offer={offer} variant="header" /> : null
}

function ApplicantOffersLayout() {
  const { offerId } = useParams({ strict: false })
  const { first, second } = useRootSearch()
  const { query } = useApplicantOffersInfinite()

  const hasOfferSelected = !!offerId
  const hasOffers = query.data.pages.flatMap((page) => page.list).length > 0

  const hasProfileSelected =
    first?.type === 'job' || first?.type === 'applicant' || second?.type === 'job' || second?.type === 'applicant'
  const hasReport = hasOfferSelected && hasProfileSelected
  // A report the section MOUNTED on was already drawn by its skeleton; only one opened from the list slides in.
  const [openedWithSection, setOpenedWithSection] = useState(hasReport)
  if (openedWithSection && !hasReport) setOpenedWithSection(false)

  return (
    <CollapsiblePanelProvider>
      <div className="flex h-full w-full">
        {/* When no offer is selected, show the offers list / insights dashboard in the main panel */}
        {!hasOfferSelected && (
          <ScrollableContent>
            <ScrollableArea>
              <ApplicantOffersPanelContent variant="main" />
            </ScrollableArea>
          </ScrollableContent>
        )}

        {hasOfferSelected && (
          <ScrollableContent>
            <ScrollableArea>
              {hasProfileSelected ? (
                <AnimatedOutlet className="h-full" still={openedWithSection} />
              ) : hasOffers ? (
                <ApplicantOffersEmptyState type="select-offer" />
              ) : (
                <ApplicantOffersEmptyState type="no-offers" />
              )}
            </ScrollableArea>
          </ScrollableContent>
        )}
      </div>
    </CollapsiblePanelProvider>
  )
}

// Reads the `getOffersInfinite` query the `_list` layout below prefetches and renders the company offers rail list.
function OffersPanelContent(props: { variant?: 'sidebar' | 'main' }) {
  const { t } = useLingui()
  const { offerId } = useParams({ strict: false })
  const { q: searchQueryFromUrl } = useRootSearch()
  const createOfferLink = useModalLink('/@modal/offers/new')
  const findOfferLink = useModalLink('/@modal/offers/find')
  // In a stack this list is a whole level, and a pop always lands on it with the selection cleared — so its
  // hint must be on screen ALREADY while the level above is dragged off it, not appear on the commit and
  // shove the list down a row at the end of the gesture. (The rows' own selected highlight is suppressed
  // inside OfferTreeItem — see useStackedSelection.)
  const stacked = useStackedLayout()

  const { searchInput, handleSearchChange } = useDebouncedSearch({
    initialValue: searchQueryFromUrl,
  })

  // Defer the route's OPTIONS object so a search keeps the PREVIOUS list on screen while the new page loads.
  const { context } = useMatch({ from: '/_app/offers/_list' })
  const deferredOffers = useDeferredValue(context.companyOffersInfiniteQueryOptions)
  const { data, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useSuspenseInfiniteQuery(deferredOffers)
  // True while the deferred options lag the live ones (a search is in flight) — keeps the search spinner
  // / fetching bar visible during the keep-previous transition.
  const isOffersFetching = isFetching || deferredOffers !== context.companyOffersInfiniteQueryOptions

  const { loadMoreRef } = useInfiniteScroll({
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    fetchNextPage,
  })

  const allOffers = useHydrationSafePages(data?.pages ?? []).flatMap((page) => page.list)

  const totalCount = data?.pages[0]?.pagination.count ?? 0
  const canCreateOffers = data?.pages[0]?.viewerCanCreate ?? false
  // The search term that produced the list currently on screen (the DEFERRED `q`, not the live URL
  // one). Empty-state decisions key off this so clearing the search jumps straight to the original
  // list instead of flashing the cold "No offers yet" state while the stale search list is still shown.
  // Deferred separately from the options above, but off the same source, so the two move together.
  const activeQuery = useDeferredValue(searchQueryFromUrl)
  const showOnlyEmptyState = totalCount === 0 && !activeQuery

  const isMainVariant = props.variant === 'main'

  if (isMainVariant) {
    return (
      <div className="flex h-full flex-col">
        {!showOnlyEmptyState && (
          <>
            <div className="flex items-center gap-2 border-b p-3">
              <SearchInput
                placeholder={t`Search offers`}
                value={searchInput}
                onChange={handleSearchChange}
                isLoading={isOffersFetching && searchQueryFromUrl.length > 0}
              />
              {canCreateOffers && (
                <ButtonLink variant="secondary" {...createOfferLink}>
                  <PlusIcon className="size-4" />
                  {t`Add`}
                </ButtonLink>
              )}
            </div>
          </>
        )}

        <ScrollableList
          isFetching={isOffersFetching}
          loadMoreRef={loadMoreRef}
          isFetchingMore={isFetchingNextPage}
          isEmpty={allOffers.length === 0}
          emptyState={
            <PanelEmptyState
              noResults={allOffers.length === 0 && !!activeQuery}
              icon={<NAVIGATION_ICONS.offers className="text-muted-foreground size-5" />}
              title={t`No offers yet`}
              description={t`Add offers to get started`}
              action={
                canCreateOffers ? (
                  <ButtonLink variant="secondary" {...createOfferLink}>
                    <PlusIcon className="size-4" />
                    {t`Add offer`}
                  </ButtonLink>
                ) : undefined
              }
            />
          }
          hint={
            allOffers.length > 0 && (
              <SelectionHint
                visible={stacked || !offerId}
                text={t`Select an offer to view its candidates and reports`}
              />
            )
          }
        >
          <SectionLabel className="px-2">
            {totalCount === 1 ? t`${totalCount} offer` : t`${totalCount} offers`}
          </SectionLabel>
          {allOffers.map((offer) => (
            <OfferTreeItem key={offer.id} offer={offer} isSelected={offerId === offer.id.toString()} />
          ))}
        </ScrollableList>
      </div>
    )
  }

  return (
    <>
      {!showOnlyEmptyState && (
        <>
          <CollapsibleToolbar>
            <CollapsibleToolbar.Search searchLink={findOfferLink} searchLabel={t`Search offers`}>
              <SearchInput
                placeholder={t`Search offers`}
                value={searchInput}
                onChange={handleSearchChange}
                isLoading={isOffersFetching && searchQueryFromUrl.length > 0}
              />
            </CollapsibleToolbar.Search>
            {canCreateOffers && (
              <CollapsibleToolbar.Action
                icon={<PlusIcon className="size-4" />}
                tooltip={t`Add offer`}
                link={createOfferLink}
              />
            )}
          </CollapsibleToolbar>
        </>
      )}

      <ScrollableList
        isFetching={isOffersFetching}
        loadMoreRef={loadMoreRef}
        isFetchingMore={isFetchingNextPage}
        isEmpty={allOffers.length === 0}
        hint={
          allOffers.length > 0 && (
            <SelectionHint visible={stacked || !offerId} text={t`Select an offer to view its candidates and reports`} />
          )
        }
        emptyState={
          <PanelEmptyState
            noResults={allOffers.length === 0 && !!activeQuery}
            icon={<NAVIGATION_ICONS.offers className="text-muted-foreground size-5" />}
            title={t`No offers yet`}
            description={t`Add offers to get started`}
            action={
              <ButtonLink variant="secondary" {...createOfferLink}>
                <PlusIcon className="size-4" />
                {t`Add offer`}
              </ButtonLink>
            }
          />
        }
      >
        <CollapsibleSectionLabel>
          {totalCount === 1 ? t`${totalCount} offer` : t`${totalCount} offers`}
        </CollapsibleSectionLabel>
        {allOffers.map((offer) => (
          <OfferTreeItem key={offer.id} offer={offer} isSelected={offerId === offer.id.toString()} />
        ))}
      </ScrollableList>
    </>
  )
}

function ApplicantOffersEmptyState(props: { type: 'no-offers' | 'select-offer' }) {
  const { t } = useLingui()

  return (
    <div className="flex h-dvh flex-col items-center justify-center gap-4 px-8 text-center">
      <div className="bg-muted rounded-full p-4">
        {props.type === 'no-offers' ? (
          <BriefcaseIcon className="text-muted-foreground size-8" />
        ) : (
          <SearchIcon className="text-muted-foreground size-8" />
        )}
      </div>
      <div className="flex flex-col gap-2">
        <h3 className="text-lg font-medium">{props.type === 'no-offers' ? t`No offers found` : t`Select an offer`}</h3>
        {props.type === 'no-offers' ? (
          <p className="text-muted-foreground max-w-[480px] text-sm">
            {t`Search for offers to compare your profile and find the best opportunities for you.`}
          </p>
        ) : (
          <div className="flex items-center gap-1.5">
            <SearchIcon className="text-muted-foreground/70 size-3.5" />
            <p className="text-muted-foreground/70 text-xs">{t`Select an offer to view their report`}</p>
          </div>
        )}
      </div>
    </div>
  )
}
